import React, { useEffect, useState } from 'react';
import styled, { css, CSSProperties } from 'styled-components';
import { useTestId } from '../../hooks/useTestId';
import { Icon, IconNames } from '../../Components/Icons/Icon';
import { IThemePalette } from '../../Themes/interfaces';
import { baseFontCss, defaultNormalTextCss, inputFocused } from '../../common/mixins';

export const inputColours = (palette: IThemePalette) => ({
  input: {
    placeholder: palette.primary.text[25],
    focus: {
      background: palette.primary.second[25],
      border: palette.primary.second[25]
    },
    disabled: {
      border: palette.primary.text[20],
      background: palette.neutral.gray[100]
    },
    invalid: {
      text: palette.alerts.alert,
      border: palette.alerts.alert
    }
  }
});

export interface IBaseFormControlProps {
  invalid?: boolean;
}

export interface IInputProps extends IBaseFormControlProps, React.InputHTMLAttributes<HTMLInputElement>, Pick<CSSProperties, 'textAlign'> {}

const baseFormControlCss = css<IBaseFormControlProps>`
  ${defaultNormalTextCss};

  height: 3rem;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colours.default.border};
  border-radius: 0.25rem;
  margin-top: 0.5rem;
  align-self: auto;
  caret-color: ${({ theme }) => theme.colours.default.caret};
  background-color: ${({ theme }) => theme.colours.default.background};
  padding: 1rem;
  box-sizing: border-box;
  cursor: auto;
  outline: 0;

  &:-webkit-autofill::first-line,
  &:-webkit-autofill {
    font-size: ${({ theme }) => theme.text.sizeLarge};
    ${baseFontCss};
  }

  ::-ms-reveal {
    display: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.colours.input.placeholder};
  }

  :disabled {
    border: 1px solid ${({ theme }) => theme.colours.input.disabled.border};
    background-color: ${({ theme }) => theme.colours.input.disabled.background};
  }

  :focus:not(disabled) {
    ${inputFocused}
  }

  ${(props: IBaseFormControlProps) =>
    props.invalid &&
    css`
      border: 1px solid ${({ theme }) => theme.colours.input.invalid.border};
      color: ${({ theme }) => theme.colours.input.invalid.text};
    `}
`;

export const FormControlBase = styled.input<IInputProps>`
  ${baseFormControlCss}

  ${({ textAlign }) =>
    textAlign &&
    css`
      text-align: ${textAlign};
    `}
`;

export const Input = React.forwardRef<HTMLInputElement, Omit<IInputProps, 'ref'>>(function FormControl(props, ref) {
  const { generateTestId } = useTestId(props, 'Input');
  const [isInvalid, setIsInvalid] = useState(props.invalid);
  useEffect(() => setIsInvalid(props.invalid), [props.invalid]);
  const onChange = (e: React.ChangeEvent<any>) => {
    if (props.required) {
      setIsInvalid(!e.target.value);
    }

    if (props.onChange) {
      props.onChange(e);
    }
  };
  const onBlur = (e: React.FocusEvent<any>) => {
    if (props.required) {
      setIsInvalid(!e.target.value);
    }
    if (props.onBlur) {
      props.onBlur(e);
    }
  };
  return <FormControlBase {...props} onChange={onChange} invalid={isInvalid} ref={ref} onBlur={onBlur} {...generateTestId()} />;
});

export const FormControlIcon = styled(Icon)`
  display: flex;
  cursor: pointer;
  margin-top: -2.5rem;
  margin-left: calc(100% - 2rem);
  margin-bottom: 1.5rem;
`;

type PasswordInputType = 'text' | 'password';

export const PasswordInput: React.FC<Omit<IInputProps, 'type'>> = (props) => {
  const { generateTestId } = useTestId(props, 'PasswordInput');
  const [inputType, setInputType] = useState<PasswordInputType>('password');

  return (
    <>
      <Input {...props} type={inputType} {...generateTestId()} />
      <FormControlIcon
        name={inputType === 'text' ? IconNames.Eye : IconNames.EyeOff}
        onClick={() => setInputType((value) => (value === 'text' ? 'password' : 'text'))}
        {...generateTestId('Icon')}
      />
    </>
  );
};

const removeSpinnerOnWebkitCss = css`
  /* Required css to remove spinner on Webkit browsers */
  &&::-webkit-outer-spin-button,
  &&::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const LargeFormControl = styled(Input)`
  text-align: center;
  padding: 1rem 0.5rem;
  ${({ type }) =>
    type === 'number' &&
    css`
      ${removeSpinnerOnWebkitCss}
    `}
`;

const CustomNumericInput = styled(Input)`
  appearance: textfield;
  ${removeSpinnerOnWebkitCss}
`;

export const NumericInput = React.forwardRef<HTMLInputElement, Omit<IInputProps, 'ref' | 'type'>>(function NumericInput(props, ref) {
  const { generateTestId } = useTestId(props, 'NumericInput');
  return <CustomNumericInput type='number' inputMode='numeric' {...props} {...generateTestId()} ref={ref} />;
});

export const TextArea = styled.textarea`
  ${baseFormControlCss}
`;
